import { ELocale } from '@/i18n/localeEnum';

export type IDeployParams = {
  websiteDomain: string;
  wpDomain: string;
};

export type DeployEnv = 'local' | 'development' | 'staging-ssr' | 'production';

type LocaleHosts = {
  websiteDomains: Record<DeployEnv, string>;
  wpDomain: string;
};

const ENV: DeployEnv = (() => {
  switch (process.env.NEXT_PUBLIC_ENV) {
    case 'local': return 'local';
    case 'development': return 'development';
    case 'staging': return 'staging-ssr';
    default: return 'production';
  }
})();

const LOCALE_HOSTS: Record<ELocale, LocaleHosts> = {
  [ELocale.EN_US]: {
    websiteDomains: {
      local: 'https://ots-local.onthesnow.com',
      development: 'https://ots-dev.onthesnow.com',
      'staging-ssr': 'https://stage-ssr-us.onthesnow.com',
      production: 'https://www.onthesnow.com',
    },
    wpDomain: 'https://newsonthesnow.com/',
  },
  [ELocale.DE_DE]: {
    websiteDomains: {
      local: 'https://ots-local.skiinfo.de',
      development: 'https://ots-dev.skiinfo.de',
      'staging-ssr': 'https://stage-ssr-de.skiinfo.de',
      production: 'https://www.skiinfo.de',
    },
    wpDomain: 'https://de.newsonthesnow.com/',
  },
  [ELocale.EN_GB]: {
    websiteDomains: {
      local: 'https://ots-local.onthesnow.co.uk',
      development: 'https://ots-dev.onthesnow.co.uk',
      'staging-ssr': 'https://stage-ssr-uk.onthesnow.co.uk',
      production: 'https://www.onthesnow.co.uk',
    },
    wpDomain: 'https://uk.newsonthesnow.com/',
  },
  [ELocale.FR_FR]: {
    websiteDomains: {
      local: 'https://ots-local.skiinfo.fr',
      development: 'https://ots-dev.skiinfo.fr',
      'staging-ssr': 'https://stage-ssr-fr.skiinfo.fr',
      production: 'https://www.skiinfo.fr',
    },
    wpDomain: 'https://fr.newsonthesnow.com/',
  },
  [ELocale.IT_IT]: {
    websiteDomains: {
      local: 'https://ots-local.skiinfo.it',
      development: 'https://ots-dev.skiinfo.it',
      'staging-ssr': 'https://stage-ssr-it.skiinfo.it',
      production: 'https://www.skiinfo.it',
    },
    wpDomain: 'https://it.newsonthesnow.com/',
  },
  [ELocale.PL_PL]: {
    websiteDomains: {
      local: 'https://ots-local.skiinfo.pl',
      development: 'https://ots-dev.skiinfo.pl',
      'staging-ssr': 'https://stage-ssr-pl.skiinfo.pl',
      production: 'https://www.skiinfo.pl',
    },
    wpDomain: 'https://pl.newsonthesnow.com/',
  },
  [ELocale.CS_CZ]: {
    websiteDomains: {
      local: 'https://ots-local.onthesnow.cz',
      development: 'https://ots-dev.onthesnow.cz',
      'staging-ssr': 'https://stage-ssr-cz.onthesnow.cz',
      production: 'https://www.onthesnow.cz',
    },
    wpDomain: 'https://cz.newsonthesnow.com/',
  },
  [ELocale.SK_SK]: {
    websiteDomains: {
      local: 'https://ots-local.onthesnow.sk',
      development: 'https://ots-dev.onthesnow.sk',
      'staging-ssr': 'https://stage-ssr-sk.onthesnow.sk',
      production: 'https://www.onthesnow.sk',
    },
    wpDomain: 'https://sk.newsonthesnow.com/',
  },
  [ELocale.NL_NL]: {
    websiteDomains: {
      local: 'https://ots-local.skiinfo.nl',
      development: 'https://ots-dev.skiinfo.nl',
      'staging-ssr': 'https://stage-ssr-nl.skiinfo.nl',
      production: 'https://www.skiinfo.nl',
    },
    wpDomain: 'https://nl.newsonthesnow.com/',
  },
  [ELocale.NN_NO]: {
    websiteDomains: {
      local: 'https://ots-local.skiinfo.no',
      development: 'https://ots-dev.skiinfo.no',
      'staging-ssr': 'https://stage-ssr-no.skiinfo.no',
      production: 'https://www.skiinfo.no',
    },
    wpDomain: 'https://no.newsonthesnow.com/',
  },
  [ELocale.ES_ES]: {
    // ES uses 'es.skiinfo.com' (the country code is the host prefix in prod, baked into the
    // local/dev domains too). Staging-SSR uses 'stage-ssr-es.skiinfo.com' — no es. prefix
    // because the stage-ssr- prefix is the disambiguator.
    websiteDomains: {
      local: 'https://ots-local.es.skiinfo.com',
      development: 'https://ots-dev.es.skiinfo.com',
      'staging-ssr': 'https://stage-ssr-es.skiinfo.com',
      production: 'https://es.skiinfo.com',
    },
    wpDomain: 'https://es.newsonthesnow.com/',
  },
  [ELocale.SV_SE]: {
    websiteDomains: {
      local: 'https://ots-local.skiinfo.se',
      development: 'https://ots-dev.skiinfo.se',
      'staging-ssr': 'https://stage-ssr-se.skiinfo.se',
      production: 'https://www.skiinfo.se',
    },
    wpDomain: 'https://se.newsonthesnow.com/',
  },
  [ELocale.DA_DK]: {
    websiteDomains: {
      local: 'https://ots-local.skiinfo.dk',
      development: 'https://ots-dev.skiinfo.dk',
      'staging-ssr': 'https://stage-ssr-dk.skiinfo.dk',
      production: 'https://www.skiinfo.dk',
    },
    wpDomain: 'https://dk.newsonthesnow.com/',
  },
};

const buildEnvConfig = (env: DeployEnv): Record<ELocale, IDeployParams> => (Object.keys(LOCALE_HOSTS) as ELocale[]).reduce(
  (acc, locale) => {
    const host = LOCALE_HOSTS[locale];
    acc[locale] = { websiteDomain: host.websiteDomains[env], wpDomain: host.wpDomain };
    return acc;
  },
  {} as Record<ELocale, IDeployParams>,
);

export const localeLocalConfig = buildEnvConfig('local');
export const localeDevelopmentConfig = buildEnvConfig('development');
export const localeStagingSSRConfig = buildEnvConfig('staging-ssr');
export const localeProductionConfig = buildEnvConfig('production');

export const localeConfig = buildEnvConfig(ENV);
